import React, { useState } from "react";
import { BatteryCharging, Car, Building2, Home, CheckCircle2, Zap, Settings, IndianRupee, TrendingUp, Clock, MapPin, Users, Shield, Gauge } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { submitLead } from "../lib/api";
import { toast } from "sonner";
import CTABanner from "../components/CTABanner";
import SEO from "../components/SEO";

export default function EVChargingPage() {
  const [form, setForm] = useState({ name: "", phone: "", city: "", message: "" });
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.phone) return toast.error("Name and phone required");
    setLoading(true);
    try {
      await submitLead({ ...form, source: "ev" });
      toast.success("Thanks! Our EV team will reach out.");
      setForm({ name: "", phone: "", city: "", message: "" });
    } catch {
      toast.error("Failed to submit. Try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div data-testid="ev-page">
      <SEO title="EV Charging Solutions" description="AC & DC EV chargers for home, office and commercial stations. CNGTPL Solar installs OCPP-compliant, fast EV charging across India with revenue-share options." />
      <section
        className="relative py-20 lg:py-28 text-white"
        style={{
          backgroundImage: "linear-gradient(rgba(15,23,42,0.8), rgba(15,23,42,0.65)), url(https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=1800&q=70)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="container-narrow grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="badge-eco bg-blue-600/20 border-blue-500/40 text-blue-300 mb-4">EV Charging</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight mb-5">
              Charge smarter. <span className="text-yellow-400">Drive cleaner.</span>
            </h1>
            <p className="text-lg text-slate-200 max-w-xl mb-5">
              End-to-end EV charging infrastructure — from a 3.3 kW home charger to 120 kW DC fast-charging stations. Solar-integrated, OCPP-compliant, and revenue-generating for commercial operators.
            </p>
            <div className="flex flex-wrap gap-5 text-sm">
              <div className="flex items-center gap-2 text-slate-200"><Shield className="w-4 h-4 text-yellow-400" /> OCPP 1.6 Certified</div>
              <div className="flex items-center gap-2 text-slate-200"><TrendingUp className="w-4 h-4 text-yellow-400" /> Revenue Share PPA</div>
              <div className="flex items-center gap-2 text-slate-200"><Clock className="w-4 h-4 text-yellow-400" /> 5-day Install</div>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 text-slate-900 shadow-2xl space-y-3" data-testid="ev-enquiry-form">
            <h3 className="font-heading text-xl font-semibold">Enquire about EV charging</h3>
            <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="ev-name" />
            <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="ev-phone" />
            <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="ev-city" />
            <Input placeholder="Home / Commercial?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="ev-message" />
            <Button type="submit" disabled={loading} className="w-full bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-semibold" data-testid="ev-submit">{loading ? "Sending..." : "Request Callback"}</Button>
          </form>
        </div>
      </section>

      {/* Use cases */}
      <section className="section-pad">
        <div className="container-narrow">
          <div className="text-center mb-12">
            <div className="badge-eco mb-3 mx-auto">Use Cases</div>
            <h2 className="text-3xl sm:text-4xl font-heading font-semibold text-slate-900">EV charging for every scenario</h2>
            <p className="text-slate-600 mt-3 max-w-2xl mx-auto">From a single home charger to a multi-port highway station — we design, supply and install the right solution for your need.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                icon: Home,
                title: "Home EV Charger",
                desc: "Type 2 / Bharat AC chargers at 3.3 / 7.4 / 22 kW. Wall-mounted or pedestal.",
                tag: "Residential",
                specs: ["3.3 kW – 22 kW AC", "Type 2 / Bharat AC", "Wi-Fi + app control", "Single / 3-phase", "₹22,000 – ₹85,000"],
              },
              {
                icon: Building2,
                title: "Workplace & Apartment",
                desc: "Multi-port AC chargers with RFID access + per-user billing for offices and housing societies.",
                tag: "Workplace",
                specs: ["4 / 8 / 16 port variants", "RFID card access", "Per-user billing", "Load balancing", "₹2 – 8 Lakh per station"],
              },
              {
                icon: Car,
                title: "Commercial / Highway Station",
                desc: "30-120 kW DC fast chargers with CCS2, CHAdeMO, GBT. Revenue-generating public charging.",
                tag: "Commercial",
                specs: ["30 / 60 / 120 kW DC", "CCS2 + CHAdeMO + GBT", "OCPP 1.6 + cloud billing", "Payment gateway + app", "₹12 – 35 Lakh per unit"],
              },
            ].map((s) => (
              <div key={s.title} className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-yellow-400 hover:shadow-lg transition">
                <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center mb-4">
                  <s.icon className="w-6 h-6" />
                </div>
                <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold mb-1">{s.tag}</div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">{s.desc}</p>
                <ul className="space-y-1.5 text-xs text-slate-600">
                  {s.specs.map((spec) => (
                    <li key={spec} className="flex gap-2"><span className="text-yellow-500">▸</span> {spec}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Profit calculator for commercial station */}
      <section className="section-pad bg-slate-50">
        <div className="container-narrow">
          <div className="text-center mb-10">
            <div className="badge-amber mb-3 mx-auto">Revenue Model</div>
            <h2 className="text-3xl sm:text-4xl font-heading font-semibold text-slate-900">Why a commercial EV station is a goldmine</h2>
            <p className="text-slate-600 mt-3 max-w-2xl mx-auto">The EV revolution is creating India's next cash-cow business — the public charging station. Here's the typical payback math for a 60 kW DC fast charger.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
              <h3 className="font-heading text-2xl font-semibold text-slate-900 mb-5 flex items-center gap-2">
                <IndianRupee className="w-6 h-6 text-emerald-600" /> Typical 60 kW DC Station
              </h3>
              <div className="space-y-3 text-sm">
                {[
                  { k: "Station investment", v: "₹18 – 22 Lakh", color: "text-slate-900" },
                  { k: "Civil + canopy + signage", v: "₹3 – 4 Lakh", color: "text-slate-900" },
                  { k: "Total setup cost", v: "₹22 – 26 Lakh", color: "text-slate-900 font-bold" },
                  { k: "Input tariff (commercial)", v: "₹9 / unit", color: "text-slate-600" },
                  { k: "Selling tariff to EV owner", v: "₹18 / unit", color: "text-emerald-700 font-semibold" },
                  { k: "Net margin per unit", v: "₹9 / unit", color: "text-emerald-700 font-bold" },
                  { k: "Avg utilisation (4-6 hrs/day)", v: "~200 units/day", color: "text-slate-600" },
                  { k: "Daily gross profit", v: "~₹1,800", color: "text-emerald-700" },
                  { k: "Monthly net profit (after bills)", v: "₹40,000 – 60,000", color: "text-emerald-700 font-bold" },
                  { k: "Annual profit", v: "₹5 – 7 Lakh", color: "text-emerald-700 font-bold" },
                  { k: "Payback period", v: "3.5 – 5 years", color: "text-blue-700 font-bold" },
                ].map((r) => (
                  <div key={r.k} className="flex justify-between items-center py-2 border-b border-slate-100">
                    <span className="text-slate-600">{r.k}</span>
                    <span className={r.color}>{r.v}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-4">*Numbers depend on location, footfall & state tariffs. CNGTPL Solar's team provides detailed ROI modelling per site.</p>
            </div>

            <div className="bg-slate-900 text-white rounded-2xl p-6 sm:p-8 border-l-4 border-yellow-400">
              <h3 className="font-heading text-2xl font-semibold mb-5 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-yellow-400" /> Why right now?
              </h3>
              <ul className="space-y-4 text-sm">
                {[
                  { t: "EV adoption is exploding", d: "EV sales in India grew 120% YoY in 2025. Projected to be 30% of all car sales by 2030 (~3 crore EVs on road)." },
                  { t: "Charger-to-EV ratio is 1:40 (vs 1:10 needed)", d: "India needs 15 lakh public chargers by 2030. Currently we have ~25,000. Massive under-supply." },
                  { t: "FAME-II + state subsidies", d: "Up to 70% capital subsidy for public charging stations in many states (Delhi, Telangana, Kerala, MP)." },
                  { t: "High-margin service business", d: "Unlike fuel stations (5-7% margin), EV charging is a 40-50% gross margin service business." },
                  { t: "Revenue-share PPA available", d: "If you have a prime location, CNGTPL Solar can fund 100% of the station cost via revenue-share. Zero investment from your side." },
                ].map((b) => (
                  <li key={b.t} className="pl-4 border-l-2 border-yellow-400">
                    <div className="font-heading font-semibold text-yellow-400 mb-0.5">{b.t}</div>
                    <div className="text-slate-300">{b.d}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Space requirements */}
      <section className="section-pad">
        <div className="container-narrow">
          <div className="text-center mb-10">
            <div className="badge-eco mb-3 mx-auto">Space & Infrastructure</div>
            <h2 className="text-3xl sm:text-4xl font-heading font-semibold text-slate-900">What does an EV station need?</h2>
            <p className="text-slate-600 mt-3 max-w-2xl mx-auto">Before you commit, here's exactly what space, power and paperwork are required.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: MapPin,
                title: "Space",
                items: ["Home: 2 × 1.5 m (wall-mount)", "Workplace: 1 parking bay per charger", "Commercial: 6 × 4 m per DC charger", "Station canopy: 6 × 12 m for 2 ports"],
              },
              {
                icon: Zap,
                title: "Power Supply",
                items: ["Home 3.3 kW: single-phase, 15A", "Home 7.4 kW: 3-phase, 32A", "Commercial 22 kW AC: 3-phase, 63A", "60 kW DC: 3-phase, 125A (80 kVA transformer)"],
              },
              {
                icon: Settings,
                title: "Civil Works",
                items: ["Concrete foundation for DC chargers", "Cable trenching + conduits", "Canopy for shade/rain protection", "Signage + parking demarcation"],
              },
              {
                icon: Shield,
                title: "Approvals",
                items: ["DISCOM load sanction", "CEIG for 22 kW+ stations", "Fire NoC (commercial)", "Municipal corporation for signage"],
              },
            ].map((s) => (
              <div key={s.title} className="rounded-2xl border border-slate-200 p-5 bg-white">
                <div className="w-10 h-10 rounded-xl bg-yellow-100 text-yellow-700 flex items-center justify-center mb-3">
                  <s.icon className="w-5 h-5" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-3">{s.title}</h3>
                <ul className="space-y-2 text-xs text-slate-600">
                  {s.items.map((i) => <li key={i} className="flex gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" /> {i}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-pad bg-slate-50">
        <div className="container-narrow">
          <div className="text-center mb-10">
            <div className="badge-eco mb-3 mx-auto">Why choose us</div>
            <h2 className="text-3xl sm:text-4xl font-heading font-semibold text-slate-900">Benefits of CNGTPL Solar EV charging</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              "OCPP 1.6 certified",
              "24/7 remote monitoring",
              "Solar + EV integration",
              "Govt subsidy support (FAME-II)",
              "Revenue-share PPA model",
              "Pan-India installation",
              "Smartphone app access",
              "3-year comprehensive warranty",
              "Integrated payment gateway",
              "Dynamic pricing (TOD tariff)",
              "Driver mobile app (PlugIn)",
              "Dedicated account manager",
            ].map((b) => (
              <div key={b} className="flex gap-3 bg-white rounded-xl border border-slate-200 p-4">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                <span className="text-sm text-slate-700 font-medium">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-pad">
        <div className="container-narrow">
          <div className="text-center mb-10">
            <div className="badge-eco mb-3 mx-auto">Installation Process</div>
            <h2 className="text-3xl sm:text-4xl font-heading font-semibold text-slate-900">From enquiry to ready-to-charge in 5 days</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-5">
            {[
              { icon: Gauge, title: "Site Survey", desc: "Load audit & feasibility check" },
              { icon: Settings, title: "Install", desc: "Cabling, mounting, commissioning" },
              { icon: BatteryCharging, title: "Configure", desc: "OCPP setup + app onboarding" },
              { icon: CheckCircle2, title: "Go Live", desc: "Training & handover" },
            ].map((p, i) => (
              <div key={p.title} className="relative rounded-2xl bg-gradient-to-br from-slate-900 to-blue-900 text-white p-6">
                <div className="stat-number text-4xl font-bold text-yellow-400/60 absolute top-3 right-4">0{i + 1}</div>
                <p.icon className="w-8 h-8 mb-4 text-yellow-400" />
                <div className="font-heading font-semibold text-lg mb-1">{p.title}</div>
                <div className="text-sm text-slate-300">{p.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who can start */}
      <section className="section-pad bg-gradient-to-br from-slate-900 to-blue-900 text-white">
        <div className="container-narrow">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="badge-amber mb-3">Business Opportunity</div>
              <h2 className="text-3xl sm:text-4xl font-heading font-semibold mb-4">Who can start an EV charging station?</h2>
              <p className="text-slate-300 mb-5">If you own or control any of these spaces, you already have a running EV charging business waiting to happen.</p>
              <ul className="space-y-3 text-sm">
                {[
                  "Petrol pump owners — 50+ sqm spare space",
                  "Restaurant / dhaba owners on highways",
                  "Hotel & resort owners (customer retention)",
                  "Shopping mall & cinema owners",
                  "Parking lot operators (airports, stations)",
                  "Society & apartment RWAs",
                  "Fleet operators (Ola, Uber, delivery, e-rickshaw)",
                  "Industrial estate owners",
                ].map((b) => (
                  <li key={b} className="flex gap-2 items-start text-slate-200"><CheckCircle2 className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" /> {b}</li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-800 rounded-2xl p-6 border border-slate-700">
              <h3 className="font-heading text-2xl font-semibold text-yellow-400 mb-4">Ready to become an EV station operator?</h3>
              <p className="text-slate-300 text-sm mb-5">Our EV team provides an end-to-end setup — from site feasibility and FAME-II subsidy applications to commissioning and revenue-sharing models.</p>
              <div className="space-y-3 text-sm">
                {[
                  "Free site feasibility report (within 48 hours)",
                  "Complete civil + electrical + charger installation",
                  "FAME-II + state subsidy paperwork",
                  "CNGTPL PlugIn app listing (1000+ active users)",
                  "Cloud billing + payment gateway setup",
                  "24×7 remote monitoring + first-line support",
                ].map((i) => (
                  <div key={i} className="flex gap-2 text-slate-300"><Users className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" /> {i}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTABanner title="Ready to install an EV charger?" subtitle="Talk to our EV specialist for a custom charger recommendation and profitability analysis." primaryLabel="Book Site Survey" primaryTo="/contact" />
    </div>
  );
}
